import styled from 'styled-components';
import { useState } from 'react';
import { CenterScreenModal, LoadingSpinner, StatusPill } from '../atoms';
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { FlattenedOwnerData } from '@/custom-types';
import { capitalizeFirstLetter } from '@/helpers';
import { ButtonWithDropDownMenu } from '../molecules';
import { pathURL } from '@/components/constants/urls';
import { useNavigate } from 'react-router';
import {
  GetOwnersQuery,
  OwnerFilterInput,
  useDeleteOwnerMutation,
  useGetOwnersQuery
} from '@/graphql/generated';
import { Col, Row } from 'react-grid-system';
import { ConfirmationDialog } from '../organism';
import textsData from '@/textData/OwnerListTexts.json'

type OwnerListTableProps = {
  filters: OwnerFilterInput;
};
const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg} ;
  padding-bottom: ${({ theme }) => theme.spacing.lg} ;
  border-radius: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr 0.5fr 64px;
`;

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`;

const StyledGridRow = styled.div`
  display: contents;

  & > div {
    padding: 15px;
    background-color: ${({ theme }) => theme.colors.light};
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: 1px solid lightgray;
  }

  &:nth-of-type(odd) > div {
    background-color: ${({ theme }) => theme.colors.background2};
  }
`;

type OwnerData = NonNullable<GetOwnersQuery['owners']>['items'][number]

const dataFlatter = (owners: OwnerData[] | undefined): FlattenedOwnerData[]  => {
  if (!owners || owners.length === 0) return []

  return owners.map((owner) => ({
    firstName: owner.firstName,
    lastName: owner.lastName,
    phoneNumber: owner.phoneNumber,
    isActive: owner.isActive,
    id: owner.id,
    email: '',
    street: owner.address.street,
    city: owner.address.city?.name ?? '',
    state: owner.address.state?.name ?? '',
  }))
}

export const OwnerListTable = ({ filters }: OwnerListTableProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en
  
  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { open: errorHandle } = useErrorHandlerContext()
  const { setListState } = useListNavigationStateContext();

  const navigate = useNavigate()
  
  const [deleteOwner] = useDeleteOwnerMutation()
  const [ownerIdToDelete, setOwnerIdToDelete] = useState<string | null>(null)
  
  const {data, error, refetch, loading} = useGetOwnersQuery({
    variables: {
      filterInput: filters,
    },
    fetchPolicy: 'network-only',
  })

  const tableData = dataFlatter(data?.owners?.items)
  
  const confirmDeleteHandle = () => {
    if (!ownerIdToDelete) return

    deleteOwner({ variables: { id: ownerIdToDelete }})
      .then((response) => {
        confirmDialogModalOpen({
          title: texts.deleteConfirmation.title,
          description: `${response.data?.deleteOwner.firstName} ${response.data?.deleteOwner.firstName} ${texts.deleteConfirmation.description}`,
        })
        refetch()
      })
      .catch(errorHandle)
      .finally(() => setOwnerIdToDelete(null))
  }

  const onDeleteHandle = (id: string) => {
    setOwnerIdToDelete(id)
  }

  const onEditHandle = (id: string) => {
      navigate(`/${pathURL.USER}/${pathURL.OWNER}/${pathURL.CREATE}`, {
        state: {
          ownerId: id,
          readOnly: false,
        }
      })
    }

  const onCreateContractHandle = (id: string, firstName: string, lastName: string) => {
    navigate(`/${pathURL.USER}/${pathURL.CONTRACT}/owner/create`, {
      state: {
        ownerId: id,
        ownerFirstName: firstName,
        ownerLastName: lastName,
      }
    })
  }
  
  const onDetailConsultingHandle = (id: string) => {
    setListState('owners', {
      currentPage: parseInt(filters.page || '1'),
      filters,
      selectedItemId: id,
    });
    navigate(`/${pathURL.USER}/${pathURL.OWNER}/${pathURL.CREATE}`, {
      state: {
        ownerId: id,
        readOnly: true,
      }
    })
  }

  if (loading) {
    return (
      <Row justify='center'>
        <Col xs='content' >
          <LoadingSpinner text/>
        </Col>
      </Row>
    )
  }

  if (tableData.length === 0) {
    return (
      <Row>
        <Col>
          <span><b>{texts.noData}</b></span>
        </Col>
      </Row>
    )
  }

  if (error) errorHandle(error)

  return (
    <>
      <StyledGridTable>
        {/* Encabezado */}
        <StyledGridHeader>
          <div>{texts.name}</div>
          <div>{texts.phone}</div>
          <div>{texts.status}</div>
          <div></div>
        </StyledGridHeader>

        {/* Filas dinámicas */}
        {tableData.map((data) => (
          <StyledGridRow key={data.id}>
            <div>{`${capitalizeFirstLetter(data.firstName)} ${capitalizeFirstLetter(data.lastName)}`}</div>
            <div>{data.phoneNumber}</div>
            <div><StatusPill isActive={data.isActive} /></div>
            <div>
              <ButtonWithDropDownMenu
                options={[
                  {item: texts.dropdownMenu.deatils, action: () => onDetailConsultingHandle(data.id)},
                  {item: texts.dropdownMenu.edit, action: () => onEditHandle(data.id)},
                  {item: texts.dropdownMenu.delete, action: () => onDeleteHandle(data.id)},
                  {item: texts.dropdownMenu.createContract, action: () => onCreateContractHandle(data.id, data.firstName, data.lastName)},
                ]}
                position='left'
              />
            </div>
          </StyledGridRow>
        ))}
      </StyledGridTable>

      <CenterScreenModal
        isOpen={ownerIdToDelete !== null}
        onClose={() => setOwnerIdToDelete(null)}
      >
        <ConfirmationDialog
          title={texts.deletePrompt.title}
          description={texts.deletePrompt.description}
          confirmButton={{
            action: confirmDeleteHandle,
            text: texts.deletePrompt.confirm,
          }}
          cancelButton={{
            action: () => setOwnerIdToDelete(null),
            text: texts.deletePrompt.cancel,
          }}
        />
      </CenterScreenModal>
    </>
  );
};
